'use client';

import { useMemo } from 'react';
import type { ScopeTree } from '@/lib/api-types';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The portion of the scope tree the caller can administer — the option source
 * for the admin-area ConfigSwitcher. A CER is offered when the user is admin
 * of the CER itself (all its configs) or admin/referent of at least one of its
 * configurations (those only). Superadmin sees everything. A CER without
 * configurations is still offered to its admin: the switcher says so.
 */
export function useAdminScopeTree(): { tree: ScopeTree; loading: boolean } {
	const { tree, loading } = useScopeTree();
	const role = useAuthStore((s) => s.role);
	const availableOrgs = useAuthStore((s) => s.availableOrgs);

	const adminTree = useMemo(() => {
		// Una CER senza configurazioni resta: aperta da «Dettagli», Soci deve
		// restare su di lei (e dire che manca la configurazione) invece di
		// ripiegare in silenzio sulla prima CER dell'elenco.
		if (role === 'superadmin') return tree;
		const roleById = new Map(availableOrgs.map((o) => [o.id, o.role]));
		const adminish = (r?: string) => r === 'admin' || r === 'referent';
		const cerAdmin = (id: string) => roleById.get(id) === 'admin';
		return tree
			.map((cer) =>
				cerAdmin(cer.id)
					? cer
					: { ...cer, configs: cer.configs.filter((cfg) => adminish(roleById.get(cfg.id))) },
			)
			.filter((cer) => cerAdmin(cer.id) || cer.configs.length > 0);
	}, [tree, role, availableOrgs]);

	return { tree: adminTree, loading };
}
